import { SubmitButton } from "@/components/Global/Button/Button";
import styles from "./RecipeFilterModal.module.scss";
import { useGlobalContext } from "@aleksundshantu.com/aunds-utils-react/contexts";
import { useAppContext } from "@/context/AppContext";
import { useModal } from "@/components/_primitives/Modal/ModalRoot/ModalRoot";
import BackgroundBubbles from "@/components/BackgroundBubbles/BackgroundBubbles";
import useWindowSize from "@/hooks/useWindowSize";
import { FilterModalCloseButton } from "./General/FilterModalCloseButton";
import { OptionGrid } from "./FilterSections/OptionGrid";
import { FormEvent, useState } from "react";
import { SortedCategoriesType } from "@/utils/rezeptAPI/getRecipeFilterOptions";
import { useRouter } from "next/router";
import { updateQueryParamsFromFilters } from "@/modules/RecipeGrid/queryParamsHelper";
type ReturnTypeMap = {
  categories: number[] | null;
  level: number | null;
};

const TABLET_WIDTH = 768;

export const RecipeFilterModal = ({
  filterOptions,
}: {
  filterOptions: SortedCategoriesType | null;
}) => {
  const { setFormModalType } = useAppContext();
  const { getLabel } = useGlobalContext();
  const { width } = useWindowSize();
  const { setOpen } = useModal();
  const router = useRouter();

  // TODO: get this from Backend
  const DIFFICULTY_FILTER_OPTIONS = [1, 2, 3].map((level) => ({
    id: level,
    label: getLabel(`difficulty_level_${level}_label`),
  }));

  const allCategories =
    filterOptions?.print_categories.map((cat) => ({
      id: cat.rezept_print_kategorie_id,
      label: cat.print_kategorie,
    })) ?? [];

  const getInitialValues = <T extends "categories" | "level">(
    param: T,
  ): ReturnTypeMap[T] => {
    const value = router.query[param];
    if (typeof value !== "string") return null;

    return param === "categories"
      ? (value.split(",").map(Number) as ReturnTypeMap[T])
      : (Number(value) as ReturnTypeMap[T]);
  };

  const [selectedCategories, setSelectedCategories] = useState<number[]>(
    getInitialValues("categories") ?? [],
  );
  const [selectedDifficulty, setSelectedDifficulty] = useState<number | null>(
    getInitialValues("level"),
  );

  const handleClose = () => {
    setFormModalType("text");
    setOpen(false);
  };

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault();
    updateQueryParamsFromFilters(
      { categories: selectedCategories, difficultyLevel: selectedDifficulty },
      router,
    );
    handleClose();
  };

  return (
    <form
      data-lenis-prevent
      className={styles["modal-filter-form"]}
      onSubmit={handleSubmit}
    >
      {width < TABLET_WIDTH && (
        <FilterModalCloseButton handleClick={handleClose} />
      )}

      <div className={styles["headline-wrapper"]}>
        <div className={`${styles["headline"]}`}>
          <span role="heading" aria-level={2}>
            Rezepte filtern
          </span>
          {width < TABLET_WIDTH && (
            <BackgroundBubbles topLeft={false} topRight={false} />
          )}
        </div>
      </div>

      <div className={styles["filter-section"]}>
        <OptionGrid
          title={getLabel("recipe_categories_filter_header")}
          options={allCategories}
          shouldCollapse={width < TABLET_WIDTH}
          selectedValues={selectedCategories}
          onChange={(option) => {
            setSelectedCategories((prev) => {
              if (!prev.includes(option)) {
                return [...prev, option];
              } else {
                const copy = [...prev];
                const index = copy.indexOf(option);
                copy.splice(index, 1);

                return copy;
              }
            });
          }}
        />
        <OptionGrid
          title={getLabel("recipe_difficulty_level_filter_header")}
          options={DIFFICULTY_FILTER_OPTIONS}
          shouldCollapse={width < TABLET_WIDTH}
          selectedValues={selectedDifficulty ? [selectedDifficulty] : []}
          onChange={(option) => {
            setSelectedDifficulty(
              option !== selectedDifficulty ? option : null,
            );
          }}
          addDifficultyIcons
        />
      </div>

      <SubmitButton className={styles["submit-button"]} icon="circleCheck">
        {getLabel("recipe_filter_submit_button_label")}
      </SubmitButton>
    </form>
  );
};
